import { faEye, faEyeSlash, faDoorOpen, faUserGroup } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Typography } from '@vapor/material'
import { Stack, Tooltip } from '@vapor/material'
import { t } from 'i18next'
import { useEffect, useMemo } from 'react'
import { Trans } from 'react-i18next'
import { HicIconButton } from 'src/app/components/hic-vapor/button/icon-button'
import { HiCIcon } from 'src/app/components/icons/icon'
import useBreakpoints from 'src/app/hooks/breakpoints/use-breakpoints'
import useFormatCurrency from 'src/app/hooks/current-locale/use-format-currency'
import useLocalizeString from 'src/app/hooks/current-locale/use-localized-string'
import { ReservationAccordionHeaderProps } from './reservation-accordion-header'

export default function ReservationAccordionHeaderMobile(props: ReservationAccordionHeaderProps) {
  const { isOpen, isDisabled, startingFromAvg, startingFromTotal, isPage, currency, resource, onToggleAccordion, resourceUnavailable, resourceTypeAvailabilityString } = props
  const { md } = useBreakpoints()
  const { formatCurrency } = useFormatCurrency()
  const localizeString = useLocalizeString()

  const optionButtonTitle: string = useMemo(() => {
    return isOpen ? t('pms.booking.reservation_new.hide_rate_options') : t('pms.booking.reservation_new.show_rate_options')
  }, [isOpen])

  useEffect(() => {
    if (isDisabled && isOpen) {
      onToggleAccordion()
    }
  }, [isDisabled])

  return (
    <Box mb={0.5}>
      <Stack
        direction="column"
        style={{ pointerEvents: 'auto' }}
        onClick={(event) => {
          event.stopPropagation()
          onToggleAccordion()
        }}
      >
        <Stack direction="row" spacing={1}>
          <Typography noWrap variant="titleXSmall" style={{ marginTop: 0, marginBottom: 1, width: 'calc(100%)' }}>
            <b>{resource.resourceType.code ?? localizeString(resource.resourceType.customName ?? resource.resourceType.name.description)}</b>
          </Typography>
          <Stack direction={'row'} spacing={md && isPage ? 3 : 2} mr={0} justifyContent="flex-end" alignItems="center">
            {startingFromAvg !== undefined && startingFromTotal !== undefined && (
              <>
                <Stack direction="column" justifyContent="center">
                  <Typography variant="caption" justifyContent="center">
                    {t('pms.booking.reservation_new.starting_from')}
                  </Typography>
                </Stack>

                <Stack direction="row" spacing={1}>
                  <Stack direction="column" justifyContent="center" spacing={0}>
                    <Typography variant="bodyLarge700">{formatCurrency(startingFromTotal, currency)}</Typography>
                  </Stack>
                </Stack>
              </>
            )}
            {startingFromAvg === undefined && startingFromTotal === undefined && (
              <Stack direction="column" justifyContent="center" alignContent="center">
                <Typography variant="body" justifyContent="center">
                  <b>{t('pms.booking.reservation_new.best_price_button.unavailable_resources')}</b>
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1}>
            <Stack direction="row" alignItems="end" spacing={1}>
              <HiCIcon>
                <FontAwesomeIcon icon={faUserGroup} />
              </HiCIcon>

              <Typography variant="caption">
                <Trans
                  i18nKey="pms.booking.reservation_new.max_person"
                  values={{ current: resource.resourceType.persons, max: resource.resourceType.persons + resource.resourceType.additionalPersons }}
                />
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1} style={{ alignItems: 'baseline' }}>
              <Stack direction="row" alignItems="end">
                <HiCIcon pr={1}>
                  <FontAwesomeIcon icon={faDoorOpen} color={resourceUnavailable(resource.resourceType.id) ? 'red' : 'black'} />
                </HiCIcon>
                <Typography variant="caption" color={resourceUnavailable(resource.resourceType.id) ? 'red' : 'black'}>
                  {resourceTypeAvailabilityString(resource.resourceType.id, resource.inventory)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>

          {startingFromAvg !== undefined && startingFromTotal !== undefined && (
            <Tooltip arrow title={!isOpen ? t('pms.booking.reservation_new.open_accordion.tooltip') ?? '' : ''}>
              <div>
                <HicIconButton
                  disabled={isDisabled}
                  startIcon={<FontAwesomeIcon icon={isOpen ? faEyeSlash : faEye} />}
                  variant={isOpen ? 'outlined' : 'contained'}
                  size="small"
                  style={{
                    pointerEvents: 'auto',
                  }}
                  onClick={(event) => {
                    event.stopPropagation()
                    onToggleAccordion()
                  }}
                >
                  {optionButtonTitle}
                </HicIconButton>
              </div>
            </Tooltip>
          )}
        </Stack>

        {resourceUnavailable(resource.resourceType.id) && (
          <Stack direction="row">
            <Typography variant="caption" color="red">
              {t('pms.booking.reservation_new.resource_type_unavailable_description')}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  )
}
